import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Package,
  Clock,
  CheckCircle,
  Truck,
  XCircle,
  Eye,
  ChevronDown,
  RefreshCw,
  MapPin,
  CreditCard,
} from "lucide-react";
import toast from "react-hot-toast";
import api from "../../config/api";
import { getImageUrl } from "../../utils/helpers";
import { openProductById } from "../../utils/productNavigation";
import OrderTrackingPanel from "./OrderTrackingPanel";
import {
  ORDER_STATUS_META,
  TERMINAL_ORDER_STATUSES,
  getOrderStatusColor,
  getOrderStatusLabel,
  getOrderStatusProgress,
} from "../../utils/orderStatusUtils";

const OrderHistory = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const orderStatuses = [
    { value: "pending", label: "Awaiting Payment", color: "yellow", icon: Clock },
    { value: "paid", label: "Payment Confirmed", color: "green", icon: CheckCircle },
    {
      value: "confirmed",
      label: "Confirmed",
      color: "blue",
      icon: CheckCircle,
    },
    {
      value: "processing",
      label: "Processing",
      color: "indigo",
      icon: Package,
    },
    { value: "shipped", label: "Shipped", color: "purple", icon: Truck },
    {
      value: "delivered",
      label: "Delivered",
      color: "green",
      icon: CheckCircle,
    },
    { value: "cancelled", label: "Cancelled", color: "red", icon: XCircle },
    { value: "refunded", label: "Refunded", color: "gray", icon: XCircle },
  ];

  useEffect(() => {
    const controller = new AbortController();

    const fetchOrders = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          page: pagination.page,
          limit: pagination.limit,
          ...(statusFilter !== "all" && { status: statusFilter }),
        });

        const response = await api.get(`/orders/user?${params}`, {
          signal: controller.signal,
          skipGlobalErrorToast: true,
        });
        setOrders(response.data.data || []);
        setPagination(response.data.pagination || pagination);
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("Error fetching orders:", error);
        toast.error("Failed to fetch orders");
        setOrders([]);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchOrders();

    return () => {
      controller.abort();
    };
  }, [pagination.page, statusFilter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(statusFilter !== "all" && { status: statusFilter }),
      });

      const response = await api.get(`/orders/user?${params}`, {
        skipGlobalErrorToast: true,
      });
      setOrders(response.data.data || []);
      setPagination(response.data.pagination || pagination);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => getOrderStatusColor(status);

  const getStatusIcon = (status) => {
    const statusObj = orderStatuses.find((s) => s.value === status);
    if (statusObj) return statusObj.icon;
    const normalized = ORDER_STATUS_META[status];
    if (normalized) {
      const match = orderStatuses.find((s) => s.value === status);
      return match?.icon || Clock;
    }
    return Clock;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusProgress = (status) => getOrderStatusProgress(status);

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 font-poppins">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-6xl mx-auto"
      >
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center font-outfit">
                <Package className="mr-3 text-primary-600" />
                My Orders
              </h1>
              <p className="text-gray-600 mt-2 font-poppins">
                Track and manage your order history
              </p>
            </div>
            <button
              onClick={fetchOrders}
              disabled={loading}
              className="flex items-center justify-center px-4 py-2 bg-black text-white font-medium font-poppins hover:bg-gray-800 disabled:opacity-50 border-0"
              style={{ borderRadius: 0, boxShadow: "none" }}
            >
              <RefreshCw
                className={`w-4 h-4 mr-2 text-primary-600 ${
                  loading ? "animate-spin" : ""
                }`}
                style={{ borderRadius: 0, boxShadow: "none" }}
              />
              Refresh
            </button>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white border p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <label className="text-sm font-medium text-gray-700 font-poppins">
              Filter by status:
            </label>
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 focus:ring-2 focus:ring-black focus:border-transparent appearance-none bg-white pr-10 font-poppins"
              >
                <option value="all">All Orders</option>
                {orderStatuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Orders List */}
        <div className="space-y-4 sm:space-y-6">
          {loading ? (
            <div className="bg-white border p-8 sm:p-12 text-center">
              <div className="w-8 h-8 border-3 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-600 font-poppins">
                Loading your orders...
              </p>
            </div>
          ) : orders.length === 0 ? (
            <div className="bg-white border p-8 sm:p-12 text-center">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2 font-outfit">
                No orders found
              </h3>
              <p className="text-gray-600 font-poppins">
                You haven't placed any orders yet.
              </p>
            </div>
          ) : (
            orders.map((order) => {
              const StatusIcon = getStatusIcon(order.status);
              const statusColor = getStatusColor(order.status);
              const progress = getStatusProgress(order.status);

              return (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white border overflow-hidden hover:shadow-md transition-shadow"
                >
                  {/* Order Header */}
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-black flex items-center justify-center">
                              <Package className="w-5 h-5 text-white" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-gray-900 font-outfit">
                              Order #{order.order_number}
                            </h3>
                            <p className="text-sm text-gray-500 font-poppins">
                              {formatDate(order.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900 font-outfit">
                            {formatCurrency(order.total_price)}
                          </p>
                          <p className="text-sm text-gray-500 font-poppins">
                            {order.order_items?.length || 0} items
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowOrderModal(true);
                          }}
                          className="flex items-center px-3 py-2 text-sm font-medium bg-black text-white font-poppins hover:bg-gray-800 transition-colors"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Details
                        </button>
                        {order.order_token && (
                          <Link
                            to={`/track/${order.order_token}`}
                            className="flex items-center px-3 py-2 text-sm font-medium border border-black text-black font-poppins hover:bg-gray-50 transition-colors"
                          >
                            <Truck className="w-4 h-4 mr-1" />
                            Track
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Order Status */}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <StatusIcon className={`w-5 h-5 text-primary-600`} />
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium bg-primary-100 text-primary-800 font-poppins`}
                        >
                          {getOrderStatusLabel(order.status)}
                        </span>
                      </div>
                      {!TERMINAL_ORDER_STATUSES.includes(order.status) && (
                        <span className="text-sm text-gray-500">
                          {Math.round(progress)}% Complete
                        </span>
                      )}
                    </div>

                    {/* Progress Bar */}
                    {!TERMINAL_ORDER_STATUSES.includes(order.status) && (
                      <div className="w-full bg-gray-200 h-2 mb-4">
                        <div
                          className="bg-primary-600 h-2 transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    )}

                    {/* Order Items Preview */}
                    {order.order_items && order.order_items.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-900 font-outfit">
                          Items in this order:
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {order.order_items.slice(0, 3).map((item, index) => (
                            <div
                              key={index}
                              className="flex items-center space-x-2 bg-gray-50 px-3 py-2"
                            >
                              <img
                                src={getImageUrl(item.image)}
                                alt={item.name}
                                className="w-8 h-8 object-cover"
                              />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 truncate font-poppins">
                                  {item.name}
                                </p>
                                <p className="text-xs text-gray-500 font-poppins">
                                  Qty: {item.quantity}
                                </p>
                              </div>
                            </div>
                          ))}
                          {order.order_items.length > 3 && (
                            <div className="flex items-center bg-gray-50 px-3 py-2">
                              <span className="text-sm text-gray-600 font-poppins">
                                +{order.order_items.length - 3} more
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Shipping Address Preview */}
                    {order.shipping_address && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-start space-x-2">
                          <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-600 font-poppins">
                              Shipping to:
                            </p>
                            <p className="text-sm text-gray-900 truncate font-poppins">
                              {order.shipping_address.address}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white border p-4 sm:p-6 mt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div className="text-sm text-gray-700 text-center sm:text-left font-poppins">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
                of {pagination.total} orders
              </div>
              <div className="flex justify-center sm:justify-end space-x-2">
                <button
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                  }
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-poppins"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                  }
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-1 border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-poppins"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Order Detail Modal */}
        {showOrderModal && selectedOrder && (
          <OrderDetailModal
            order={selectedOrder}
            onClose={() => {
              setShowOrderModal(false);
              setSelectedOrder(null);
            }}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            getStatusColor={getStatusColor}
            getStatusIcon={getStatusIcon}
          />
        )}
      </motion.div>
    </div>
  );
};

// Order Detail Modal Component
const OrderDetailModal = ({
  order,
  onClose,
  formatCurrency,
  formatDate,
  getStatusColor,
  getStatusIcon,
}) => {
  const navigate = useNavigate();
  const StatusIcon = getStatusIcon(order.status);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 font-outfit">
              Order #{order.order_number}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {/* Order Status */}
          <div className="bg-gray-50 p-4">
            <div className="flex items-center space-x-3 mb-3">
              <StatusIcon className={`w-6 h-6 text-primary-600`} />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 font-outfit">
                  Order Status
                </h3>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium bg-primary-100 text-primary-800 font-poppins`}
                >
                  {getOrderStatusLabel(order.status)}
                </span>
              </div>
            </div>
            <p className="text-sm text-gray-600 font-poppins">
              Order placed on {formatDate(order.created_at)}
            </p>
          </div>

          {/* Order Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-blue-50 p-4">
              <h3 className="font-semibold text-blue-900 mb-2 font-outfit">
                Total Amount
              </h3>
              <p className="text-xl sm:text-2xl font-bold text-blue-800 font-outfit">
                {formatCurrency(order.total_price)}
              </p>
            </div>
            <div className="bg-green-50 p-4">
              <h3 className="font-semibold text-green-900 mb-2 font-outfit">
                Items
              </h3>
              <p className="text-xl sm:text-2xl font-bold text-green-800 font-outfit">
                {order.order_items?.length || 0}
              </p>
            </div>
            <div className="bg-purple-50 p-4">
              <h3 className="font-semibold text-purple-900 mb-2 font-outfit">
                Payment
              </h3>
              <p className="text-purple-800 capitalize font-poppins">
                {order.payment_status}
              </p>
            </div>
          </div>

          <OrderTrackingPanel order={order} formatDate={formatDate} />

          {/* Shipping Address */}
          {order.shipping_address && (
            <div className="bg-white border border-gray-200 p-4 sm:p-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center font-outfit">
                <MapPin className="w-5 h-5 mr-2 text-gray-500" />
                Shipping Address
              </h3>
              <div className="text-gray-700 font-poppins">
                <p className="font-medium">{order.shipping_address.address}</p>
                <p className="text-sm">
                  {order.shipping_address.city},{" "}
                  {order.shipping_address.postalCode}
                </p>
                <p className="text-sm">{order.shipping_address.country}</p>
              </div>
            </div>
          )}

          {/* Order Items */}
          {order.order_items && order.order_items.length > 0 && (
            <div className="bg-white border border-gray-200 p-4 sm:p-6">
              <h3 className="font-semibold text-gray-900 mb-3 font-outfit">
                Order Items
              </h3>
              <div className="space-y-3">
                {order.order_items.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-4 p-3 border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={async () => {
                      const ok = await openProductById(
                        item.product_id,
                        item.variant_id,
                        navigate,
                      );
                      if (!ok) {
                        toast.error("Product is no longer available");
                      }
                    }}
                  >
                    {item.image ? (
                      <img
                        src={getImageUrl(item.image)}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                        <span className="text-sm text-gray-500 font-medium text-center px-1">
                          {item.name
                            .split(" ")
                            .slice(0, 2)
                            .map((word) => word.charAt(0))
                            .join("")
                            .toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 hover:text-blue-600 transition-colors font-poppins">
                        {item.name}
                      </h4>
                      <p className="text-sm text-gray-600 font-poppins">
                        Quantity: {item.quantity}
                      </p>

                      {/* Variant Details */}
                      {item.variant_sku && (
                        <p className="text-sm text-blue-600 font-medium">
                          SKU: {item.variant_sku}
                        </p>
                      )}

                      {/* Variant Attributes */}
                      {item.variant_attributes &&
                        Object.keys(item.variant_attributes).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {Object.entries(item.variant_attributes)
                              .slice(0, 3)
                              .map(([key, value]) => (
                                <span
                                  key={key}
                                  className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 font-poppins"
                                >
                                  {key}: {value}
                                </span>
                              ))}
                            {Object.keys(item.variant_attributes).length >
                              3 && (
                              <span className="text-xs text-gray-500 font-poppins">
                                +
                                {Object.keys(item.variant_attributes).length -
                                  3}{" "}
                                more
                              </span>
                            )}
                          </div>
                        )}

                      <p className="text-xs text-gray-500 mt-1 hover:text-blue-600 transition-colors font-poppins">
                        Click to view product details →
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium font-poppins">
                        {formatCurrency(item.price)}
                      </p>
                      <p className="text-sm text-gray-600 font-poppins">
                        Total: {formatCurrency(item.price * item.quantity)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Price Breakdown */}
          <div className="bg-gray-50 p-4 sm:p-6">
            <h3 className="font-semibold text-gray-900 mb-3 font-outfit">
              Price Breakdown
            </h3>
            <div className="space-y-2 font-poppins">
              <div className="flex justify-between">
                <span>Items Total:</span>
                <span>{formatCurrency(order.items_price || 0)}</span>
              </div>

              {Number(order.discount_amount) > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>
                    Discount{order.coupon_code ? ` (${order.coupon_code})` : ""}
                  </span>
                  <span>-{formatCurrency(order.discount_amount)}</span>
                </div>
              )}

              <div className="flex justify-between">
                <span>Shipping:</span>
                <span>{formatCurrency(order.shipping_price || 0)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total:</span>
                <span>{formatCurrency(order.total_price)}</span>
              </div>
              <div className="text-center mt-2">
                <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded-full">
                  Inclusive of all taxes
                </span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default OrderHistory;
